import { 
  Plane, 
  Hotel, 
  Calendar, 
  Utensils, 
  Car, 
  MapPin, 
  Clock, 
  Star, 
  ArrowRight,
  Info,
  DollarSign
} from 'lucide-react';

const ItemIcon = ({ name }) => {
  switch (name) {
    case 'flight': return <Plane className="w-5 h-5 text-sun-500" />;
    case 'stay': return <Hotel className="w-5 h-5 text-sun-500" />;
    case 'event': return <Calendar className="w-5 h-5 text-sun-500" />;
    case 'restaurant': return <Utensils className="w-5 h-5 text-sun-500" />;
    case 'rental_car': return <Car className="w-5 h-5 text-sun-500" />;
    default: return <Info className="w-5 h-5 text-sun-500" />;
  }
};

const PackageItem = ({ item, index }) => {
  return (
    <div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
      className="group relative bg-white rounded-3xl overflow-hidden -shadow-sm hover:shadow-xl transition-all duration-500 -border -border-sun-100"
    >
      <div className="flex flex-col md:flex-row h-full">
        {/* Image Section */}
        <div className="relative w-full md:w-72 h-48 md:h-auto overflow-hidden">
          <img 
            src={item.image_url} 
            alt={item.name} 
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
            referrerPolicy="no-referrer"
          />
          <div className="absolute top-4 left-4 p-2 bg-white/90 backdrop-blur-sm rounded-full shadow-lg">
            <ItemIcon name={item.name} />
          </div>
        </div>

        {/* Content Section */}
        <div className="flex-1 p-8">
          <div className="flex flex-col justify-between h-full">
            <div>
              <div className="flex items-center justify-between mb-4">
                <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-sun-500">
                  {item.name.replace('_', ' ')}
                </span>
                {item.rating && (
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className={`w-3 h-3 ${i < item.rating ? 'fill-sun-400 text-sun-400' : 'text-neutral-200'}`} />
                    ))}
                  </div>
                )}
              </div>
              
              <h3 className="text-2xl font-serif mb-2 group-hover:text-sun-500 transition-colors">
                {item.brand_name || item.departure_airport || item.event_name_and_tagline || item.restaurant_name || item.rental_company || "Journey Detail"}
              </h3>

              {item.html_details ? (
                <div 
                  className="ql-editor text-sm text-neutral-600 line-clamp-3 mb-4"
                  dangerouslySetInnerHTML={{ __html: item.html_details }}
                />
              ) : (
                <p className="text-sm text-neutral-500 mb-4 italic">No additional details provided.</p>
              )}
            </div>

            <div className="flex flex-wrap gap-4 pt-4 border-t border-neutral-100">
              {item.departure_date && (
                <div className="flex items-center gap-2 text-xs text-neutral-500">
                  <Calendar className="w-3.5 h-3.5" />
                  <span>{item.departure_date}</span>
                </div>
              )}
              {item.check_in_date && (
                <div className="flex items-center gap-2 text-xs text-neutral-500">
                  <Clock className="w-3.5 h-3.5" />
                  <span>Check-in: {item.check_in_date}</span>
                </div>
              )}
              {item.pick_up_location && (
                <div className="flex items-center gap-2 text-xs text-neutral-500">
                  <MapPin className="w-3.5 h-3.5" />
                  <span>{item.pick_up_location}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function Type5PackagePreview(props) {

    const {
        package_item,
    } = props;

    const {
        view_theme
    } = package_item;

  const pkg = package_item;

  const __THEME_COLORS = {
        sunshine: {
            primary: "yellow",
            secondary: "orange",
            text: "black",
        },
        breeze: {
            primary: "skyblue",
            secondary: "slateblue",
            text: "black"
        },
        greenade: {
            primary: "lightgreen",
            secondary: "yellowgreen",
            text: "black"
        },
        ashey: {
            primary: "#eee",
            secondary: "darkgrey",
            text: "black"
        },
        lemonade: {
            primary: "#FFFACD",
            secondary: "#e4ffa6ff",
            text: "black"
        },
        darkula: {
            primary: "black",
            secondary: "grey",
            text: "white"
        }
    }

  return (
    <div className={"min-h-screen bg-["+__THEME_COLORS[view_theme]?.primary+"]"}>
      {/* Hero Section 
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-sun-100 px-6 py-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center text-sm font-sans tracking-widest uppercase font-medium">
          <div className="text-sun-500 font-bold">ais explorer</div>
          <div className="flex gap-8">
            <a href="#" className="hover:text-sun-500 transition-colors">Packages</a>
            <a href="#" className="hover:text-sun-500 transition-colors">Destinations</a>
            <a href="#" className="hover:text-sun-500 transition-colors">About</a>
          </div>
          <button className="bg-sun-400 hover:bg-sun-500 text-neutral-900 px-6 py-2 rounded-full transition-all text-xs font-bold shadow-sm">
            BOOK NOW
          </button>
        </div>
      </nav>*/}

      {/* Main Content */}
      <main className="-pt-24 -pb-20">
        <div className="max-w-7xl mx-auto -px-2">
          
          {/* Hero Banner */}
          <section className="relative -rounded-[3rem] overflow-hidden -aspect-[21/9] mb-12 -shadow-2xl group">
            <img 
              initial={{ scale: 1.1 }}
              animate={{ scale: 1 }}
              transition={{ duration: 1.5 }}
              src={pkg.cover_picture} 
              alt={pkg.title} 
              className="w-full h-full object-cover transition-transform duration-[2s] group-hover:scale-105"
              referrerPolicy="no-referrer"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent flex flex-col justify-end p-12 md:p-20">
              <div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <span style={{backgroundColor: "yellow"}} className="px-3 py-1 text-xs font-bold rounded-full uppercase tracking-tighter">New Experience</span>
                  <span className="text-white/80 text-sm font-medium tracking-widest uppercase">{pkg.travel_destination}</span>
                </div>
                <h1 className="text-xl md:text-2xl lg:text-2xl font-serif text-white mb-6 leading-tight max-w-4xl">
                  {pkg.title}
                </h1>
                <div className="flex items-center gap-8 text-white/90">
                  <div className="flex flex-col">
                    <span className="text-[10px] uppercase tracking-widest opacity-60">Price Starting From</span>
                    <span className="text-l font-light font-serif">{pkg.price_currency.toUpperCase()} ${pkg.total_price}</span>
                  </div>
                  <div className="w-px h-10 bg-white/20" />
                  <div className="flex flex-col">
                    <span className="text-[10px] uppercase tracking-widest opacity-60">Journey Duration</span>
                    <span className="text-l font-light">Customizable</span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Overview & Description */}
          <div className="grid grid-cols-1 -lg:grid-cols-12 px-4 gap-8 mb-10">
            <div className="lg:col-span-7">
              <div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
              >
                <h2 style={{color: __THEME_COLORS[view_theme]?.text}} className="text-3xl font-serif mb-2 text-neutral-800">
                    Experience the Journey</h2>
                <div style={{color: __THEME_COLORS[view_theme]?.text}} 
                  className="ql-editor text-lg text-neutral-600 font-light leading-relaxed mb-8"
                  dangerouslySetInnerHTML={{ __html: pkg.html_description }}
                />
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { label: 'Adults', value: pkg.max_num_of_adults, icon: <ArrowRight className="w-4 h-4" /> },
                    { label: 'Children', value: pkg.max_num_of_children, icon: <ArrowRight className="w-4 h-4" /> },
                    { label: 'Infants', value: pkg.max_num_of_infants, icon: <ArrowRight className="w-4 h-4" /> },
                    { label: 'Currency', value: pkg.price_currency.toUpperCase(), icon: <DollarSign className="w-4 h-4" /> },
                  ].map((stat, i) => (
                    <div 
                        key={i} className={"p-4 rounded-2xl bg-["+__THEME_COLORS[view_theme]?.secondary+"] -border -border-sun-100 -shadow-sm"}>
                      <div style={{color: __THEME_COLORS[view_theme]?.text}}
                        className="text-sun-500 mb-1">
                        {stat.icon}</div>
                      <div style={{color: __THEME_COLORS[view_theme]?.text}}
                        className="text-xs text-neutral-400 uppercase tracking-widest leading-none mb-1">{stat.label}</div>
                      <div style={{color: __THEME_COLORS[view_theme]?.text}}
                        className="text-lg font-semibold">{stat.value}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Sidebar / Quick Facts */}
            <div className="lg:col-span-7">
              <div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className={"bg-["+__THEME_COLORS[view_theme]?.secondary+"] rounded-[2rem] p-10 sticky top-32"}
              >
                <h3 style={{color: __THEME_COLORS[view_theme]?.text}} 
                    className="text-xl font-serif mb-6 text-neutral-800">Trip Snapshot</h3>
                <div className="space-y-6">
                  <div className="flex gap-4">
                    <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center shadow-sm border border-sun-200">
                      <Calendar className="w-5 h-5 text-sun-500" />
                    </div>
                    <div>
                      <div style={{color: __THEME_COLORS[view_theme]?.text}} 
                        className="text-xs text-neutral-400 uppercase tracking-widest">Travel Window</div>
                      <div style={{color: __THEME_COLORS[view_theme]?.text}} 
                        className="font-medium text-neutral-700">
                        {new Date(pkg.start_date) > new Date(pkg.end_date) 
                          ? `${pkg.start_date} (Flexible)` 
                          : `${pkg.start_date} to ${pkg.end_date}`}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center shadow-sm border border-sun-200">
                      <MapPin className="w-5 h-5 text-sun-500" />
                    </div>
                    <div>
                      <div style={{color: __THEME_COLORS[view_theme]?.text}}
                        className="text-xs text-neutral-400 uppercase tracking-widest">Destination</div>
                      <div style={{color: __THEME_COLORS[view_theme]?.text}}
                        className="font-medium text-neutral-700">{pkg.travel_destination}</div>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center shadow-sm border border-sun-200">
                      <Clock className="w-5 h-5 text-sun-500" />
                    </div>
                    <div>
                      <div style={{color: __THEME_COLORS[view_theme]?.text}}
                        className="text-xs text-neutral-400 uppercase tracking-widest">Status</div>
                      <div style={{color: __THEME_COLORS[view_theme]?.text}}
                        className="font-medium text-neutral-700">Limited Availability</div>
                    </div>
                  </div>
                </div>

                <div className="hidden mt-10 p-6 bg-sun-300/40 rounded-2xl -border -border-sun-300">
                  <p className="text-sm italic text-neutral-700 text-center">
                    "This package changed my perspective on wellness entirely. Highly recommended!"
                  </p>
                  <div className="mt-4 flex justify-center items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-sun-500" />
                    <span className="text-xs font-bold text-neutral-800">Sarah J.</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Timeline / Itinerary Items */}
          <section className="pb-6 px-4">
            <div className="flex items-center justify-between mb-4">
              <h2 style={{color: __THEME_COLORS[view_theme]?.text}}
                className="text-4xl font-serif text-neutral-800">Items Included</h2>
              <div className="h-px flex-1 mx-8 bg-sun-200 md:block hidden" />
              <span style={{color: __THEME_COLORS[view_theme]?.text}} className="text-xs font-bold uppercase tracking-widest text-sun-500">
                {pkg.items.length} Curated Items</span>
            </div>

            <div className="grid grid-cols-1 gap-2">
              {pkg.items.map((item, idx) => (
                  <PackageItem key={idx} item={item} index={idx} />
                ))}
            </div>
          </section>

          {/* Footer Branding 
          <footer className="pt-20 border-t border-sun-200">
            <div className="flex flex-col md:flex-row justify-between items-center gap-8">
              <div className="text-sun-500 font-bold text-2xl lowercase tracking-tighter">ais explorer.</div>
              <div className="text-sm text-neutral-400">© 2026 Sunshine Travel. All rights reserved.</div>
              <div className="flex gap-6 text-sm font-medium">
                <a href="#" className="hover:text-sun-500">Instagram</a>
                <a href="#" className="hover:text-sun-500">Twitter</a>
                <a href="#" className="hover:text-sun-500">Privacy</a>
              </div>
            </div>
          </footer>*/}

        </div>
      </main>
    </div>
  );
}